import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Divider, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ConfigurationsIcon from '@mui/icons-material/VideoSettingsOutlined';
import StorageIcon from '@mui/icons-material/Inventory2Outlined';
import { useOktaAuth } from '@okta/okta-react';
import { UserClaims } from '@okta/okta-auth-js';
import { t } from 'i18next';

import Backend from './backend/Backend';
import './NavBar.scss';
import { useTelemetry } from './tools/Telemetry';
import Util from './Util';
import AppRoutes from './AppRoutes';
import UserRoleType from './UserRoleType';
import { ReactComponent as UserManagementIcon } from './resources/images/usermanagement.svg';
import { ConfigStore } from './stores/ConfigStore';

interface NavBarProps {
    configStore: ConfigStore;
    enableScopManageUsers: Boolean;
    setEnableScopManageUsers: Function;
}

const NavBar = (props: NavBarProps) => {
    const { enableScopManageUsers, setEnableScopManageUsers } = props;
    const history = useHistory();
    const { authState, oktaAuth } = useOktaAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const [userInfo, setUserInfo] = useState<UserClaims | null>(null);
    const { trackEvent } = useTelemetry('NavBar', { identity: '' });
    const [enableSwitchCustomer, setEnableSwitchCustomer] = useState<boolean>(false);
    const [tenantInfo, setTenantInfo] = useState<any>(null);

    const currentTenant =
        Util.getSessionCurrentTenantInfo() && Util.getSessionCurrentTenantInfo().id
            ? Util.getSessionCurrentTenantInfo()
            : Util.getCurrentTenantInfo();

    useEffect(() => {
        if (!authState) {
            return;
        }

        if (!authState.isAuthenticated) {
            setUserInfo(null);
        } else {
            oktaAuth.getUser().then((info) => {
                Backend.getUserInfo(oktaAuth).then((userInfoResponse) => {
                    if (userInfoResponse.data.urole === UserRoleType.internal) {
                        setEnableSwitchCustomer(true);
                    }
                });
                setUserInfo(info);
                trackEvent({ data: { identity: info.preferred_username! } });
            });
        }
    }, [authState, oktaAuth]); // Update if authState changes

    const location = useLocation();
    const showToolbarOptions =
        location.pathname === AppRoutes.configurations ||
        location.pathname === AppRoutes.storage ||
        location.pathname === AppRoutes.user_management;
    const showUserManagement = location.pathname === AppRoutes.configurations && enableScopManageUsers;

    function handleMenu(event: any) {
        setAnchorEl(event.currentTarget);
    }

    function handleLogout() {
        handleClose();
        logout();
    }

    function handleClose() {
        setAnchorEl(null);
    }

    async function logout() {
        const basename = window.location.origin + history.createHref({ pathname: '/' });
        setEnableScopManageUsers(false);
        Util.resetCurrentTenantInfo();
        setTenantInfo(currentTenant);
        await oktaAuth.signOut({ postLogoutRedirectUri: basename });
    }

    function switchCustomer() {
        handleClose();
        setEnableScopManageUsers(false);
        history.push(`${AppRoutes.customers}switch`);
    }

    if (!authState) {
        return null;
    }

    let displayText = '';
    if (userInfo && userInfo.name) {
        displayText = `${userInfo?.name} ${currentTenant?.name ? `- ${currentTenant.name}` : ''}`;
    }

    return (
        <div className="NavBar">
            <Stack direction="column">
                <img alt="" className="Logo" src="/pub/logo.svg" />
                {showToolbarOptions ? <Divider orientation="horizontal" className="Separator" /> : null}
                {showToolbarOptions ? (
                    <Tooltip title={t('Configurations')}>
                        <IconButton
                            size="large"
                            onClick={() => {
                                history.push(AppRoutes.configurations);
                            }}
                            color="inherit"
                        >
                            <ConfigurationsIcon className="Action" />
                        </IconButton>
                    </Tooltip>
                ) : null}
                {showToolbarOptions ? (
                    <Tooltip title={t('Storage')}>
                        <IconButton
                            size="large"
                            onClick={() => {
                                history.push(AppRoutes.storage);
                            }}
                            color="inherit"
                        >
                            <StorageIcon className="Action" />
                        </IconButton>
                    </Tooltip>
                ) : null}
                {showUserManagement ? (
                    <Tooltip title={t('Manage Users For Servicce Co-Pilot')}>
                        <IconButton
                            size="large"
                            onClick={() => {
                                history.push({ pathname: AppRoutes.user_management });
                            }}
                            color="inherit"
                        >
                            <UserManagementIcon className="Action" />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <div className="BottomDock">
                    <Typography variant="body1" className="User" component="div">
                        {displayText}
                    </Typography>
                    <div>
                        <IconButton
                            data-testid="accountIconButton"
                            size="large"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenu}
                            color="inherit"
                        >
                            <AccountCircle className="Settings" />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            data-testid="menu-appbar"
                            keepMounted
                            anchorEl={anchorEl}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                        >
                            {enableSwitchCustomer ? (
                                <MenuItem data-testid="switchCustomer" onClick={switchCustomer}>
                                    Switch Customer
                                </MenuItem>
                            ) : null}
                            <MenuItem onClick={handleLogout} data-testid="logoutCustomer">
                                Logout
                            </MenuItem>
                        </Menu>
                    </div>
                </div>
            </Stack>
        </div>
    );
};
export default NavBar;
